import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ArrowUpRight, Ban, Building2, CalendarClock, CheckCircle2, Coins, FileDown, FileWarning, HardHat, Pencil, Plus, Send, ShieldCheck, TrendingUp } from "lucide-react";
import { Button } from "../ui/button";
import { StatusBadge } from "../StatusBadge";
import { euro, formatDate, openSiteReportPdf, practiceApi } from "../../lib/practiceApi";
import "./siteDashboard.css";

const DOC_STATE = { MISSING: "Mancante", EXPIRED: "Scaduto" };

const Tile = ({ icon: Icon, label, value, note, tone = "", testId }) => <div className={`site-tile ${tone}`} data-testid={testId}>
  <span className="site-tile-head"><Icon size={15} />{label}</span>
  <strong>{value}</strong>
  {note && <small>{note}</small>}
</div>;

export const SiteDashboard = ({ siteId, onOpenPractice, onOpenCoordination }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => practiceApi(`/sites/${siteId}/dashboard`).then(setData).catch(err => setError(err.message)), [siteId]);
  useEffect(() => { load(); }, [load]);

  const [notice, setNotice] = useState("");
  async function openReport(mode) {
    setError(""); setNotice("");
    try { await openSiteReportPdf(siteId, mode); }
    catch (err) { setError(err.message); }
  }
  async function sendReport() {
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await practiceApi(`/sites/${siteId}/report/send`, { method: "POST", body: JSON.stringify({ data: {} }) });
      setNotice(`Report inviato a ${result.sent_to}`);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function saveProgress(event) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await practiceApi(`/sites/${siteId}/progress`, { method: "PUT", body: JSON.stringify({
        progress_pct: Number(draft.pct || 0), progress_phase: draft.phase || "", progress_note: draft.note || "" }) });
      setEditing(false); await load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  if (error && !data) return <div className="error" role="alert" data-testid="site-dashboard-error">{error}</div>;
  if (!data) return <p className="muted" data-testid="site-dashboard-loading">Caricamento del cruscotto cantiere…</p>;

  const { progress, economics, alerts, companies, missing_docs: missing, site_docs: siteDocs, deadlines, activities, prescriptions, practices } = data;
  const pct = Math.max(0, Math.min(100, progress.pct));
  const critical = alerts.blocked_companies.length + alerts.expired_docs + alerts.overdue_deadlines;

  return <section className="site-dashboard" data-testid="site-dashboard">
    <header className="site-dashboard-head">
      <span className="site-kicker"><HardHat size={14} />CRUSCOTTO CANTIERE</span>
      <div className="site-dashboard-actions">
        <Button variant="outline" className="ghost" data-testid="site-report-studio" onClick={() => openReport("studio")}><FileDown size={14} />Report studio</Button>
        <Button variant="outline" className="ghost" data-testid="site-report-client" onClick={() => openReport("client")}><FileDown size={14} />Report committente</Button>
        <Button className="primary" disabled={busy} data-testid="site-report-send" onClick={sendReport}><Send size={14} />{busy ? "Invio…" : "Invia al committente"}</Button>
        {critical > 0
          ? <span className="site-alarm" data-testid="site-dashboard-alarm"><AlertTriangle size={14} />{critical} criticità da gestire</span>
          : <span className="site-calm" data-testid="site-dashboard-calm"><CheckCircle2 size={14} />Nessuna criticità aperta</span>}
      </div>
    </header>
    {notice && <div className="flash" role="status" data-testid="site-report-notice">{notice}</div>}

    <div className="site-progress" data-testid="site-progress">
      <div className="site-progress-top">
        <div>
          <span className="site-progress-label">Avanzamento lavori</span>
          <strong data-testid="site-progress-pct">{pct}%</strong>
          <em data-testid="site-progress-phase">{progress.phase || "Fase da definire"}</em>
        </div>
        <Button variant="outline" className="ghost" data-testid="site-progress-edit"
          onClick={() => { setDraft({ pct: progress.pct, phase: progress.phase, note: progress.note }); setEditing(!editing); }}>
          <Pencil size={14} />{editing ? "Annulla" : "Aggiorna avanzamento"}</Button>
      </div>
      <i className="site-progress-bar"><em style={{ width: `${pct}%` }} /></i>
      {progress.note && <small className="site-progress-note" data-testid="site-progress-note">{progress.note}</small>}
      {progress.updated_at && <small className="muted">Aggiornato il {formatDate(progress.updated_at.slice(0, 10))}{progress.updated_by ? ` da ${progress.updated_by}` : ""}</small>}
      {editing && <form className="site-progress-form" onSubmit={saveProgress} data-testid="site-progress-form">
        <label>Percentuale<input type="number" min="0" max="100" step="1" required data-testid="site-progress-input"
          value={draft.pct ?? 0} onChange={e => setDraft({ ...draft, pct: e.target.value })} /></label>
        <label>Fase dei lavori<select data-testid="site-progress-phase-select" value={draft.phase || ""} onChange={e => setDraft({ ...draft, phase: e.target.value })}>
          <option value="">Da definire</option>
          {progress.phases.map(phase => <option key={phase} value={phase}>{phase}</option>)}
        </select></label>
        <label className="field-wide">Nota<input maxLength={400} data-testid="site-progress-note-input" value={draft.note || ""} onChange={e => setDraft({ ...draft, note: e.target.value })} /></label>
        <Button type="submit" className="primary" disabled={busy} data-testid="site-progress-save">{busy ? "Salvataggio…" : "Salva avanzamento"}</Button>
      </form>}
    </div>

    <div className="site-tiles">
      <Tile icon={Coins} label="Totale compensi" value={euro(economics.total_fee)} note={`${practices.length} pratiche collegate`} testId="site-tile-fee" />
      <Tile icon={TrendingUp} label="Incassato" value={euro(economics.collected)} note={`${economics.collected_pct}% del totale`} tone="ok" testId="site-tile-collected" />
      <Tile icon={FileWarning} label="Da incassare" value={euro(economics.outstanding)} tone={economics.outstanding > 0 ? "warn" : "ok"} testId="site-tile-outstanding" />
      <Tile icon={Building2} label="Imprese" value={companies.length}
        note={alerts.blocked_companies.length ? `${alerts.blocked_companies.length} con ingresso bloccato` : companies.length ? "Tutte in regola" : "Nessuna impresa"}
        tone={alerts.blocked_companies.length ? "bad" : companies.length ? "ok" : ""} testId="site-tile-companies" />
      <Tile icon={ShieldCheck} label="Documenti da sistemare" value={missing.length}
        note={alerts.expired_docs ? `${alerts.expired_docs} scaduti` : "Scadenze in regola"} tone={missing.length ? "warn" : "ok"} testId="site-tile-docs" />
    </div>

    {data.is_coordination ? <>
      <div className="site-block" data-testid="site-companies-block">
        <div className="site-block-head">
          <h3><Building2 size={15} />Imprese in cantiere</h3>
          <span className="site-block-actions">
            <Button variant="outline" className="ghost" data-testid="site-add-company" onClick={() => onOpenCoordination(siteId, "company")}><Plus size={14} />Aggiungi impresa</Button>
            <Button variant="outline" className="ghost" data-testid="site-request-docs" onClick={() => onOpenCoordination(siteId, "request")}><Send size={14} />Richiedi documenti</Button>
            <Button className="primary" data-testid="site-open-coordination" onClick={() => onOpenCoordination(siteId)}>Apri coordinamento<ArrowUpRight size={14} /></Button>
          </span>
        </div>
        {companies.length ? companies.map(company => <div className="site-company" key={company.id} data-testid={`site-company-${company.id}`}>
          <span className={`site-light ${company.authorized_entry ? "green" : "red"}`} aria-hidden="true" />
          <span className="site-company-name"><strong>{company.name}</strong><small>{company.role_label || "Ruolo da definire"}</small></span>
          <span className="site-company-counts">
            <em className="ok">{company.counts.OK} ok</em>
            {!!company.counts.MISSING && <em className="warn">{company.counts.MISSING} mancanti</em>}
            {!!company.counts.EXPIRED && <em className="bad">{company.counts.EXPIRED} scaduti</em>}
          </span>
          {company.authorized_entry
            ? <span className="practice-badge success" data-testid={`site-entry-${company.id}`}>INGRESSO CONSENTITO</span>
            : <span className="practice-badge danger" data-testid={`site-entry-${company.id}`}><Ban size={11} />INGRESSO BLOCCATO</span>}
        </div>) : <p className="muted" data-testid="site-no-companies">Nessuna impresa registrata in questo cantiere.</p>}
      </div>

      <div className="site-two-cols">
        <div className="site-block" data-testid="site-missing-docs-block">
          <div className="site-block-head"><h3><FileWarning size={15} />Documenti mancanti o scaduti</h3></div>
          {missing.length ? missing.slice(0, 12).map((row, index) => <div className="site-doc-row" key={`${row.company_id}-${row.doc_type}`} data-testid={`site-missing-doc-${index}`}>
            <strong>{row.label}</strong>
            <span>{row.company_name}</span>
            <span className={`practice-badge ${row.status === "EXPIRED" ? "danger" : row.mandatory ? "warning" : "neutral"}`}>{DOC_STATE[row.status] || row.status}{row.mandatory ? " · blocca ingresso" : ""}</span>
          </div>) : <p className="muted" data-testid="site-docs-ok">Tutta la documentazione delle imprese è presente e valida.</p>}
          {!!alerts.missing_site_docs.length && <div className="practice-notice" data-testid="site-missing-site-docs">
            Documenti di cantiere da caricare: {alerts.missing_site_docs.join(", ")}
          </div>}
          {!alerts.missing_site_docs.length && !!siteDocs.length && <small className="muted">Documenti di cantiere completi ({siteDocs.length}).</small>}
        </div>

        <div className="site-block" data-testid="site-prescriptions-block">
          <div className="site-block-head"><h3><AlertTriangle size={15} />Prescrizioni aperte</h3></div>
          {prescriptions.length ? prescriptions.map(row => <div className="site-doc-row" key={`${row.log_id}-${row.index}`} data-testid={`site-prescription-${row.log_id}-${row.index}`}>
            <strong>{row.text}</strong>
            <span>Verbale del {formatDate(row.date)}</span>
            {row.deadline && <span className="practice-badge warning">entro {formatDate(row.deadline)}</span>}
          </div>) : <p className="muted" data-testid="site-no-prescriptions">Nessuna prescrizione aperta nei verbali.</p>}
        </div>
      </div>
    </> : <div className="site-block site-coordination-invite" data-testid="site-coordination-invite">
      <div>
        <h3><ShieldCheck size={15} />Coordinamento sicurezza non attivo</h3>
        <p className="muted">Attiva il coordinamento CSE/CSP su questo cantiere per gestire imprese, documentazione Allegato XVII, semaforo ingressi e verbali.</p>
      </div>
      <Button className="primary" data-testid="site-open-coordination" onClick={() => onOpenCoordination(siteId)}>Apri Coordinamento<ArrowUpRight size={14} /></Button>
    </div>}

    <div className="site-two-cols">
      <div className="site-block" data-testid="site-deadlines-block">
        <div className="site-block-head"><h3><CalendarClock size={15} />Prossime scadenze</h3></div>
        {deadlines.length ? deadlines.map((row, index) => <button className="site-deadline" key={`${row.practice_id}-${row.label}`} data-testid={`site-deadline-${index}`} onClick={() => onOpenPractice(row.practice_id)}>
          <strong>{row.label}</strong>
          <span>{row.practice_name}</span>
          <span className="site-deadline-date">{formatDate(row.date)}</span>
          <StatusBadge status={row.days < 0 ? "SCADUTA" : row.days <= 7 ? "URGENTE" : row.days <= 30 ? "IN SCADENZA" : "REGOLARE"} />
        </button>) : <p className="muted" data-testid="site-no-deadlines">Nessuna scadenza attiva sulle pratiche del cantiere.</p>}
      </div>

      <div className="site-block" data-testid="site-activities-block">
        <div className="site-block-head"><h3><CheckCircle2 size={15} />Attività aperte</h3></div>
        {activities.length ? activities.map((row, index) => <div className="site-doc-row" key={row.id} data-testid={`site-activity-${index}`}>
          <strong>{row.title}</strong>
          <span>{row.date ? formatDate(row.date) : "Data da definire"}{row.responsible ? ` · ${row.responsible}` : ""}</span>
          <StatusBadge status={row.status} />
        </div>) : <p className="muted" data-testid="site-no-activities">Nessuna attività aperta collegata al cantiere.</p>}
      </div>
    </div>

    {error && <div className="error" role="alert" data-testid="site-dashboard-inline-error">{error}</div>}
  </section>;
};
